import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IconCopy, IconDownload, IconExternalLink, IconPalette, IconRefresh } from "@tabler/icons";
import { useNegocioContext } from "contexts/NegocioContext";
import QRCode from "qrcode";
import { useRef, useState } from "react";

// Colores predefinidos para el QR
const predefinedColors = [
  { name: "Primario", color: "primary.main", display: "Azul Primario" },
  { name: "Negro", color: "#000000", display: "Negro Clásico" },
  { name: "Oscuro", color: "#1a1a1a", display: "Gris Oscuro" },
  { name: "Verde", color: "#2e7d32", display: "Verde" },
  { name: "Rojo", color: "#d32f2f", display: "Rojo" },
  { name: "Morado", color: "#7b1fa2", display: "Morado" },
];

const QRGeneratorDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const { negocio } = useNegocioContext();
  const canvasRef = useRef(null);

  const [customUrl, setCustomUrl] = useState("");
  const [selectedUrl, setSelectedUrl] = useState("");
  const [qrGenerated, setQrGenerated] = useState(false);
  const [error, setError] = useState("");

  // Opciones de personalización del QR
  const [qrOptions, setQrOptions] = useState({
    size: 256,
    margin: 2,
    errorCorrectionLevel: "M",
    foregroundColor: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
  });

  const generateQRCode = async (url) => {
    if (!url) {
      setError("Por favor ingresa una URL válida");
      return;
    }

    // Validar URL básica
    try {
      new URL(url.startsWith("http") ? url : `https://${url}`);
    } catch {
      setError("URL no válida. Debe incluir http:// o https://");
      return;
    }

    setError("");

    try {
      const canvas = canvasRef.current;
      const finalUrl = url.startsWith("http") ? url : `https://${url}`;

      // Generar el QR usando la librería qrcode con opciones personalizables
      await QRCode.toCanvas(canvas, finalUrl, {
        width: qrOptions.size,
        margin: qrOptions.margin,
        color: {
          dark: qrOptions.foregroundColor,
          light: qrOptions.backgroundColor,
        },
        errorCorrectionLevel: qrOptions.errorCorrectionLevel,
      });

      setQrGenerated(true);
      setSelectedUrl(finalUrl);
    } catch (err) {
      setError("Error al generar el código QR: " + err.message);
    }
  };

  const handleCustomUrlSubmit = async () => {
    await generateQRCode(customUrl);
  };

  const regenerateQR = async () => {
    if (selectedUrl) {
      await generateQRCode(selectedUrl);
    }
  };

  const resetOptions = () => {
    setQrOptions({
      size: 256,
      margin: 2,
      errorCorrectionLevel: "M",
      foregroundColor: theme.palette.primary.main,
      backgroundColor: theme.palette.background.paper,
    });
  };

  const downloadQR = () => {
    const canvas = canvasRef.current;
    const link = document.createElement("a");
    const fileName = `qr-${(negocio?.nombre || "negocio").toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().split("T")[0]}.png`;
    link.download = fileName;
    link.href = canvas.toDataURL("image/png", 1.0);
    link.click();
  };

  const copyToClipboard = async () => {
    try {
      const canvas = canvasRef.current;
      canvas.toBlob(async (blob) => {
        if (navigator.clipboard && window.ClipboardItem) {
          const item = new window.ClipboardItem({ "image/png": blob });
          await navigator.clipboard.write([item]);
        } else {
          // Fallback: copiar la URL del QR
          await navigator.clipboard.writeText(selectedUrl);
        }
      });
    } catch (err) {
      console.error("Error al copiar:", err);
      // Fallback: copiar la URL
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(selectedUrl);
      }
    }
  };

  const openUrl = () => {
    if (selectedUrl) {
      window.open(selectedUrl, "_blank");
    }
  };

  const handleClose = () => {
    setCustomUrl("");
    setSelectedUrl("");
    setQrGenerated(false);
    setError("");
    setQrOptions({
      size: 256,
      margin: 2,
      errorCorrectionLevel: "M",
      foregroundColor: theme.palette.primary.main,
      backgroundColor: theme.palette.background.paper,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: 500,
        },
      }}>
      <DialogTitle>
        <Typography variant="h4" component="div" sx={{ fontWeight: 600 }}>
          Generador de Códigos QR
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Crea códigos QR para compartir enlaces de tu negocio de forma fácil y profesional
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* URL personalizada */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Ingresa la URL
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                fullWidth
                label="Ingresa cualquier URL"
                placeholder="https://ejemplo.com o ejemplo.com"
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleCustomUrlSubmit()}
                variant="outlined"
              />
              <Button variant="contained" onClick={handleCustomUrlSubmit} disabled={!customUrl.trim()} sx={{ minWidth: 120 }}>
                Generar
              </Button>
            </Stack>
          </Grid>

          {/* Opciones de personalización */}
          {qrGenerated && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                <IconPalette size="1.2rem" style={{ marginRight: 8, verticalAlign: "middle" }} />
                Personalizar QR
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Tamaño: {qrOptions.size}px
                  </Typography>
                  <Slider value={qrOptions.size} onChange={(e, newValue) => setQrOptions((prev) => ({ ...prev, size: newValue }))} min={128} max={512} step={32} marks valueLabelDisplay="auto" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Margen: {qrOptions.margin}
                  </Typography>
                  <Slider value={qrOptions.margin} onChange={(e, newValue) => setQrOptions((prev) => ({ ...prev, margin: newValue }))} min={0} max={8} step={1} marks valueLabelDisplay="auto" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Corrección de Error</InputLabel>
                    <Select value={qrOptions.errorCorrectionLevel} label="Corrección de Error" onChange={(e) => setQrOptions((prev) => ({ ...prev, errorCorrectionLevel: e.target.value }))}>
                      <MenuItem value="L">Baja (L)</MenuItem>
                      <MenuItem value="M">Media (M)</MenuItem>
                      <MenuItem value="Q">Alta (Q)</MenuItem>
                      <MenuItem value="H">Muy Alta (H)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Color del QR
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {predefinedColors.map((colorOption) => {
                      const isSelected = qrOptions.foregroundColor === (colorOption.color === "primary.main" ? theme.palette.primary.main : colorOption.color);
                      const actualColor = colorOption.color === "primary.main" ? theme.palette.primary.main : colorOption.color;

                      return (
                        <Chip
                          key={colorOption.name}
                          label={colorOption.display}
                          clickable
                          variant={isSelected ? "filled" : "outlined"}
                          onClick={() =>
                            setQrOptions((prev) => ({
                              ...prev,
                              foregroundColor: actualColor,
                            }))
                          }
                          sx={{
                            borderColor: actualColor,
                            color: isSelected ? "white" : actualColor,
                            backgroundColor: isSelected ? actualColor : "transparent",
                            "&:hover": {
                              backgroundColor: isSelected ? actualColor : `${actualColor}20`,
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Button variant="outlined" startIcon={<IconRefresh />} onClick={regenerateQR} size="small">
                      Actualizar QR
                    </Button>

                    <Button variant="text" onClick={resetOptions} size="small">
                      Restablecer
                    </Button>

                    <Box sx={{ flexGrow: 1 }} />

                    <Chip label="Pro Tip: Mayor corrección permite leer el QR aunque esté dañado" size="small" color="info" variant="outlined" />
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* Error */}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          {/* Código QR generado */}
          {qrGenerated && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  textAlign: "center",
                }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Código QR Generado
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <canvas
                    ref={canvasRef}
                    width={qrOptions.size}
                    height={qrOptions.size}
                    style={{
                      border: `2px solid ${theme.palette.divider}`,
                      borderRadius: 8,
                      maxWidth: "100%",
                      height: "auto",
                    }}
                  />
                </Box>

                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    mb: 2,
                    wordBreak: "break-all",
                    maxWidth: 400,
                    mx: "auto",
                  }}>
                  {selectedUrl}
                </Typography>

                <Stack direction="row" spacing={2} justifyContent="center">
                  <Tooltip title="Descargar QR">
                    <IconButton
                      onClick={downloadQR}
                      color="primary"
                      sx={{
                        border: `1px solid ${theme.palette.primary.main}`,
                      }}>
                      <IconDownload />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Copiar imagen">
                    <IconButton
                      onClick={copyToClipboard}
                      color="secondary"
                      sx={{
                        border: `1px solid ${theme.palette.secondary.main}`,
                      }}>
                      <IconCopy />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Abrir enlace">
                    <IconButton
                      onClick={openUrl}
                      color="success"
                      sx={{
                        border: `1px solid ${theme.palette.success.main}`,
                      }}>
                      <IconExternalLink />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Paper>
            </Grid>
          )}

          {/* Ejemplos de uso */}
          {!qrGenerated && (
            <Grid item xs={12}>
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.secondary.main}08 100%)`,
                }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  💡 Ideas para usar tus QR
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      📋{" "}
                      <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>
                        En tu carta física
                      </Box>
                    </Typography>
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      📄{" "}
                      <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>
                        En facturas y recibos
                      </Box>
                    </Typography>
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      📧{" "}
                      <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>
                        En firmas de email
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      📱{" "}
                      <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>
                        En redes sociales
                      </Box>
                    </Typography>
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      🏪{" "}
                      <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>
                        En tu local físico
                      </Box>
                    </Typography>
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      📦{" "}
                      <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>
                        En packaging de productos
                      </Box>
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QRGeneratorDialog;
